import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";
import NonCompulsoryFieldsLabel from "../../../helpers/NonCompulsoryFields";

// --- Data for Dropdowns based on Cloud Provider ---
const providerData = {
    gcp: {
        regions: ["africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"],
        databases: ["alloydb", "cloudsql", "mongodb", "postgresql", "spanner"],
    },
    aws: {
        regions: ["us-east-1", "us-east-2", "us-west-1", "us-west-2", "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2", "ap-south-1", "ap-northeast-1", "ap-northeast-2", "ap-southeast-1", "ap-southeast-2", "sa-east-1"],
        databases: ["aurora", "RDS", "opensearch", "postgresql", "redis"],
    },
    azure: {
        regions: ["East US", "East US 2", "West US", "West US 2", "Central US", "North Europe", "West Europe", "Southeast Asia", "East Asia", "Australia East"],
        databases: ["azure-cache-for-redis", "azure-cosmos-db-for-mongodb", "azure-cosmos-db-for-postgresql"],
    }
};

const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];

interface CostLabelPair {
    id?: string;
    cost: string;
    label: string;
}

interface RAGFormValues {
    id?: string;
    name: string;
    environment: string;
    cloudProvider: 'gcp' | 'aws' | 'azure';
    solutionCategoryType: string;
    applicationName: string;
    projectId: string;
    region: string;
    databaseType: string;
    embeddingModelId: string;
    chunkingStrategy: string;
    chunkSize: string;
    chunkOverlap: string;
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
}

interface FetchedData {
    id: string;
    name: string;
    environment: string;
}

interface ApplicationData {
    id: string;
    applicationName: string;
    environment: string;
}

interface AddRAGPageProps {
    isEdit: boolean;
}
interface RAGFormContentProps {
    isEdit: boolean;
}

// --- Formik Helper Component ---
const FormEffects: React.FC<{
    applications: ApplicationData[];
    fetchedRAGs: FetchedData[];
    setAvailableEnvironments: (envs: string[]) => void;
    isEdit: boolean;
}> = ({ applications, fetchedRAGs, setAvailableEnvironments, isEdit }) => {
    const { values, setFieldValue } = useFormikContext<RAGFormValues>();
    const prevCloudProviderRef = useRef(values.cloudProvider);
    const { id } = useParams();
    const isMounted = useRef(false);

    useEffect(() => {
        if (prevCloudProviderRef.current !== values.cloudProvider) {
            setFieldValue("region", "");
            setFieldValue("databaseType", "");
        }
        prevCloudProviderRef.current = values.cloudProvider;
    }, [values.cloudProvider, setFieldValue]);

    useEffect(() => {
        if (values.applicationName) {
            const envsForApp = applications
                .filter(app => app.applicationName === values.applicationName)
                .map(app => app.environment);
            
            const uniqueEnvs = Array.from(new Set(envsForApp));

            const availableEnvs = uniqueEnvs.filter(env => 
                !fetchedRAGs.some(rag => 
                    rag.name === values.name &&
                    rag.environment === env &&
                    (!isEdit || id !== rag.id)
                )
            );
            
            setAvailableEnvironments(availableEnvs);

            if (isMounted.current && !availableEnvs.includes(values.environment)) {
                setFieldValue('environment', '');
            }
        } else {
            setAvailableEnvironments([]);
            if(isMounted.current) {
                setFieldValue('environment', '');
            }
        }
        
        if(!isMounted.current) {
            isMounted.current = true;
        }
    }, [values.applicationName, values.name, applications, fetchedRAGs, setAvailableEnvironments, setFieldValue, isEdit, id, values.environment]);

    return null;
}

const RAGFormContent: React.FC<RAGFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [fetchedRAGs, setFetchedRAGs] = useState<FetchedData[]>([]);
    const [applications, setApplications] = useState<ApplicationData[]>([]);
    const [availableEnvironments, setAvailableEnvironments] = useState<string[]>([]);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<RAGFormValues | null>(null);
    const [isLoadingData, setIsLoadingData] = useState(isEdit);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [ragsRes, appsRes] = await Promise.all([
                    queryGraphql(`query GetRAGs { getRAGs { id name environment } }`),
                    queryGraphql(`query GetApplications { applications { id applicationName environment } }`)
                ]);

                const ragsData = ragsRes.data?.getRAGs || ragsRes.getRAGs || [];
                if (ragsData) {
                    setFetchedRAGs(ragsData.map((p: any) => ({
                        id: p.id,
                        name: p.name,
                        environment: p.environment,
                    })));
                }

                const appsData = appsRes.data?.applications || appsRes.applications || [];
                if (appsData) {
                    setApplications(appsData);
                }

            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const fetchRAGForEdit = async () => {
            if (isEdit && id) {
                setIsLoadingData(true);
                try {
                    const response = await queryGraphql(`
                        query GetRAG($id: String!) {
                          getRAG(id: $id) {
                            id name solutionCategoryType cloudProvider environment application { applicationName } costLabelPairs { cost label }
                            details { rag { projectId region databaseType embeddingModelId chunkingStrategy chunkSize chunkOverlap } }
                          }
                        }
                    `, { id: id });

                    const rag = response.getRAG;
                    if (rag) {
                        const allPairs: { cost: string, label: string }[] = rag.costLabelPairs || [];
                        const costCenterPair = allPairs.find(p => p.cost === "cost-center");
                        const dynamicPairs = allPairs.filter(p => p.cost !== "cost-center").map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));
                        
                        setInitialFormValuesForEdit({
                            id: rag.id,
                            name: rag.name,
                            environment: rag.environment,
                            applicationName: rag.application?.applicationName || "",
                            solutionCategoryType: rag.solutionCategoryType || 'rag',
                            cloudProvider: rag.cloudProvider || 'gcp',
                            projectId: rag.details.rag.projectId,
                            region: rag.details.rag.region,
                            databaseType: rag.details.rag.databaseType,
                            embeddingModelId: rag.details.rag.embeddingModelId,
                            chunkingStrategy: rag.details.rag.chunkingStrategy,
                            chunkSize: rag.details.rag.chunkSize,
                            chunkOverlap: rag.details.rag.chunkOverlap,
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                        });
                    } else {
                        navigate("/aisolutions/rag");
                    }
                } catch (err) {
                    console.error("Error fetching RAG for editing:", err);
                    navigate("/aisolutions/rag");
                } finally {
                    setIsLoadingData(false);
                }
            }
        };
        fetchRAGForEdit();
    }, [isEdit, id, navigate]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(50, "Max 50 characters")
            .test('is-unique-rag-name-in-env', 'This RAG Name already exists in the selected environment.', function (value) {
                const { environment } = this.parent;
                const currentId = isEdit ? id : undefined;
                if (!value || !environment) return true;
                return !fetchedRAGs.some(p => p.name === value.toLowerCase() && p.environment === environment && (!currentId || p.id !== currentId));
            }),
        cloudProvider: Yup.string().required("This field is required"),
        applicationName: Yup.string().required("This field is required"),
        environment: Yup.string().required("This field is required"),
        solutionCategoryType: Yup.string().required("This field is required"),
        projectId: Yup.string().required("This field is required"),
        region: Yup.string().required("This field is required"),
        databaseType: Yup.string().required("This field is required"),
        embeddingModelId: Yup.string().required("This field is required"),
        chunkingStrategy: Yup.string().required("This field is required"),
        chunkSize: Yup.string().matches(/^[1-9]\d*$/, "Must be a positive integer").required("This field is required"),
        chunkOverlap: Yup.string().matches(/^[0-9]\d*$/, "Must be a positive integer or zero").required("This field is required"),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
    });

    const defaultInitialValues: RAGFormValues = {
        name: "",
        environment: "",
        cloudProvider: "gcp",
        solutionCategoryType: "rag",
        applicationName: "",
        projectId: "",
        region: "",
        databaseType: "",
        embeddingModelId: "",
        chunkingStrategy: "default",
        chunkSize: "1024",
        chunkOverlap: "200",
        costCenterValue: "",
        costLabelPairs: [],
    };
    
    if (isEdit && isLoadingData) {
        return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading RAG data...</Typography></Box>;
    }
    if (isEdit && !initialFormValuesForEdit) {
         return <Typography color="error" sx={{p:3}}>Could not load RAG data for editing.</Typography>;
    }

    return (
        <Container sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
            <Formik
                initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setIsSubmittingForm(true);
                    setSubmitting(true);

                    const selectedApplication = applications.find(app => app.applicationName === values.applicationName && app.environment === values.environment);
                    const applicationId = selectedApplication ? selectedApplication.id : null;
                    
                    const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

                    const commonInput = {
                        name: values.name,
                        environment: values.environment,
                        cloudProvider: values.cloudProvider,
                        solutionCategoryType: values.databaseType,
                        costLabelPairs: finalCostLabelPairs,
                        details: {
                            rag: {
                                projectId: values.projectId,
                                region: values.region,
                                databaseType: values.databaseType,
                                embeddingModelId: values.embeddingModelId,
                                chunkingStrategy: values.chunkingStrategy,
                                chunkSize: values.chunkSize,
                                chunkOverlap: values.chunkOverlap,
                            }
                        },
                        application: applicationId,
                    };

                    const mutationString = isEdit ? 
                        `mutation UpdateSolutionRAG($input: UpdateSolutionInput!) { updateSolutionRAG(input: $input) { id } }` :
                        `mutation CreateSolutionRAG($input: CreateSolutionInput!) { createSolutionRAG(input: $input) { id } }`;
                    
                    const variables = { input: isEdit ? { id: id, ...commonInput } : commonInput };
                    const resultKey = isEdit ? 'updateSolutionRAG' : 'createSolutionRAG';

                    try {
                        const data = await queryGraphql(mutationString, variables);
                        if (data[resultKey] && data[resultKey].id) {
                            navigate("/aisolutions/rag");
                        } else {
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} RAG Solution. ${JSON.stringify(data.errors)}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    } finally {
                        setIsSubmittingForm(false);
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, isValid, dirty,setFieldValue, errors, touched }) => {
                    const projectIdLabel = values.cloudProvider === 'aws' ? 'Account ID' : values.cloudProvider === 'azure' ? 'Subscription ID' : 'Project ID';
                    const currentRegionList = providerData[values.cloudProvider]?.regions || [];
                    const currentDbList = providerData[values.cloudProvider]?.databases || [];
                    const uniqueApplicationNames = Array.from(new Map(applications.map(app => [app.applicationName, app])).values());

                    return (
                        <Form>
                            <FormEffects applications={applications} fetchedRAGs={fetchedRAGs} setAvailableEnvironments={setAvailableEnvironments} isEdit={isEdit} />
                            <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
                                {isEdit ? "Edit the RAG Solution details below" : "Fill the below details to add a RAG Solution"}
                            </Typography>
                            
                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                        <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    aria-label={p.label}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        textTransform: 'none',
                                                        fontFamily: 'Gilroy-Medium',
                                                        borderRadius: '24px !important',
                                                        border: '1px solid #ccc !important',
                                                        px: 2,
                                                        py: 1,
                                                        '&.Mui-selected': {
                                                            borderColor: '#000 !important',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                        }
                                                    }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="cloudProvider" /></div>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>RAG Details</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="RAG Name" /><TextField name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter RAG Name" fullWidth variant="filled" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="name" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={projectIdLabel} /><TextField name="projectId" value={values.projectId} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${projectIdLabel}`} fullWidth variant="filled" hiddenLabel error={touched.projectId && Boolean(errors.projectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="projectId" /></div></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CompulsoryFieldsLabel fieldName="Application Name" />
                                        <TextField select variant="filled" fullWidth name="applicationName" value={values.applicationName} onChange={handleChange} onBlur={handleBlur} hiddenLabel error={touched.applicationName && Boolean(errors.applicationName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                            {uniqueApplicationNames.map((app) => (<MenuItem key={app.id} value={app.applicationName}>{app.applicationName}</MenuItem>))}
                                        </TextField>
                                        <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="applicationName" /></div>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CompulsoryFieldsLabel fieldName="Environment" />
                                        <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel disabled={!values.applicationName} error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                             <MenuItem value="" disabled>Select an environment</MenuItem>
                                             {availableEnvironments.map((env) => (<MenuItem key={env} value={env}>{env}</MenuItem>))}
                                        </TextField>
                                        <div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="environment" /></div>
                                    </Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Region" /><TextField select name="region" value={values.region} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel error={touched.region && Boolean(errors.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>{currentRegionList.map((r) => (<MenuItem key={r} value={r}>{r}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="region" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Database Type" /><TextField select name="databaseType" value={values.databaseType} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel error={touched.databaseType && Boolean(errors.databaseType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>{currentDbList.map((db) => (<MenuItem key={db} value={db}>{db}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="databaseType" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Embedding Model ID" /><TextField name="embeddingModelId" value={values.embeddingModelId} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Embedding Model ID" fullWidth variant="filled" hiddenLabel error={touched.embeddingModelId && Boolean(errors.embeddingModelId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="embeddingModelId" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Chunking Strategy" /><TextField name="chunkingStrategy" value={values.chunkingStrategy} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., default" fullWidth variant="filled" hiddenLabel error={touched.chunkingStrategy && Boolean(errors.chunkingStrategy)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="chunkingStrategy" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Chunk Size" /><TextField name="chunkSize" value={values.chunkSize} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 1024" fullWidth variant="filled" hiddenLabel error={touched.chunkSize && Boolean(errors.chunkSize)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="chunkSize" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Chunk Overlap" /><TextField name="chunkOverlap" value={values.chunkOverlap} onChange={handleChange} onBlur={handleBlur} placeholder="e.g., 200" fullWidth variant="filled" hiddenLabel error={touched.chunkOverlap && Boolean(errors.chunkOverlap)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="chunkOverlap" /></div></Grid>
                                </Grid>
                            </Box>
                            
                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3} sx={{ mb: "16px" }}>
                                    <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0" } }} /></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
                                </Grid>
                                <FieldArray name="costLabelPairs" render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
                                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
                                                <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>
                                            + Add Key Value Pair
                                        </Button>
                                    </div>
                                )} />
                            </Box>
                            
                            <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black' } }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mt: 1, mb: 2.5, display: 'flex', justifyContent: 'flex-start', '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />

                            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4 }} variant="outlined" size="large" onClick={() => navigate("/aisolutions/rag")} disabled={isSubmittingForm }>Cancel</Button>
                                <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm  || !(isValid && dirty && confirmationChecked)  || isViewer }>{(isSubmittingForm ) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New RAG")}</Button>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </Container>
    );
};

const AddRAGPage: React.FC<AddRAGPageProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"aisolutions-rag"} />
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:3 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555" }}>
                        <Link underline="hover" color="inherit" href="/rag" onClick={(e)=> {e.preventDefault(); navigate("/aisolutions/rag")}}>RAG Solutions</Link>
                        <Typography color="text.primary">{isEdit ? "Edit RAG" : "Add New RAG"}</Typography>
                    </Breadcrumbs>
                    <Box display="flex" alignItems="center" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/rag")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">{isEdit ? "Edit RAG" : "Add New RAG"}</Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
                        <RAGFormContent isEdit={isEdit} />
                    </Box>
                </Box>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddRAGPage;